<template>
  <article class="flow-manager-node" :class="`flow-manager-node-${appearance.tone}`">
    <Handle v-if="isRoot === false" type="target" :position="Position.Left" class="flow-manager-handle" />
    <div class="flow-manager-node-head">
      <span class="flow-manager-node-icon">
        <Icon :icon="appearance.icon" width="16" height="16" />
      </span>
      <span class="flow-manager-node-kind">{{ appearance.label }}</span>
      <span v-if="issueCount > 0" class="flow-manager-node-warning" :title="`${issueCount} item needs attention`">
        <Icon icon="tabler:alert-triangle" width="15" height="15" />
      </span>
    </div>
    <h3 class="flow-manager-node-title">{{ data.label }}</h3>
    <p class="flow-manager-node-summary">{{ summary }}</p>
    <div class="flow-manager-node-meta">
      <span v-if="variableCount > 0">
        <Icon icon="tabler:braces" width="13" height="13" />
        {{ variableCount }} {{ variableCount === 1 ? 'field' : 'fields' }}
      </span>
      <span v-if="buttonCount > 0">
        <Icon icon="tabler:pointer" width="13" height="13" />
        {{ buttonCount }} {{ buttonCount === 1 ? 'button' : 'buttons' }}
      </span>
    </div>
    <Handle v-if="allowsOutgoing === true" type="source" :position="Position.Right" class="flow-manager-handle" />
  </article>
</template>

<script setup>
import { computed } from 'vue';
import { Handle, Position } from '@vue-flow/core';
import { Icon } from '@iconify/vue/dist/offline';
import { getNodeAppearance, getNodeSummary } from './flowManagerModel';

const props = defineProps({
  data: {
    type: Object,
    required: true
  }
});

const appearance = computed(() => getNodeAppearance(props.data));
const summary = computed(() => getNodeSummary(props.data));
const isRoot = computed(() => props.data.nodeType === 'Root');
const allowsOutgoing = computed(() => props.data.actionType !== 'endCall');
const variableCount = computed(() => Array.isArray(props.data.variablesToCollect) === true ? props.data.variablesToCollect.length : 0);
const buttonCount = computed(() => Array.isArray(props.data.buttons) === true ? props.data.buttons.length : 0);
const issueCount = computed(() => {
  const rawCount = Number(props.data.flowManagerIssueCount);
  return Number.isFinite(rawCount) === true ? rawCount : 0;
});
</script>

<style scoped>
.flow-manager-node {
  width: 268px;
  min-height: 128px;
  padding: 0.85rem 0.9rem;
  border: 1px solid #dbe2ea;
  border-left: 3px solid #718198;
  border-radius: 0.45rem;
  background: #ffffff;
  box-shadow: 0 3px 8px rgba(15, 23, 42, 0.055);
  color: #10233e;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.flow-manager-node:hover {
  box-shadow: 0 7px 16px rgba(15, 23, 42, 0.09);
}

.flow-manager-node-start {
  border-left-color: #13805f;
}

.flow-manager-node-conversation {
  border-left-color: #2f72c7;
}

.flow-manager-node-knowledge {
  border-left-color: #7561b6;
}

.flow-manager-node-decision {
  border-left-color: #bd7514;
}

.flow-manager-node-action {
  border-left-color: #197b93;
}

.flow-manager-node-phone {
  border-left-color: #bf611c;
}

.flow-manager-node-danger {
  border-left-color: #bd3030;
}

.flow-manager-node-head {
  display: flex;
  align-items: center;
}

.flow-manager-node-icon {
  width: 1.8rem;
  height: 1.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dde6f0;
  border-radius: 0.35rem;
  background: #f5f7f9;
  color: #34516f;
  flex-shrink: 0;
}

.flow-manager-node-kind {
  margin-left: 0.5rem;
  color: #62748b;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.flow-manager-node-warning {
  margin-left: auto;
  color: #d97706;
}

.flow-manager-node-title {
  margin: 0.65rem 0 0;
  font-size: 0.86rem;
  font-weight: 650;
  line-height: 1.35;
}

.flow-manager-node-summary {
  margin: 0.35rem 0 0;
  color: #61718a;
  font-size: 0.72rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.flow-manager-node-meta {
  display: flex;
  align-items: center;
  margin-top: 0.75rem;
  min-height: 1.05rem;
  padding-top: 0.1rem;
}

.flow-manager-node-meta span {
  display: inline-flex;
  align-items: center;
  color: #66788f;
  font-size: 0.63rem;
}

.flow-manager-node-meta span + span {
  margin-left: 0.65rem;
}

.flow-manager-node-meta svg {
  margin-right: 0.2rem;
}

:deep(.flow-manager-handle) {
  width: 0.7rem;
  height: 0.7rem;
  border: 2px solid #ffffff;
  background: #3e5f83;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.22);
}
</style>
